import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Badge from "../common/Badge";
import Button from "../common/Button";
import EmptyState from "../common/EmptyState";
import Skeleton from "../common/Skeleton";
import {
  createAiConfig,
  deleteAiConfig,
  getAiConfig,
  getAiConfigs,
  getClients,
  updateAiConfig,
} from "../../services/aiConfigApi";
import { normalizeApiError } from "../../utils/errors";
import { formatDateTime } from "../../utils/formatters";

const EMPTY_FORM = {
  id: "",
  client_id: "",
  name: "",
  status: "ACTIVE",
  is_default: false,
  company_name: "",
  company_timezone: "Asia/Calcutta",
  agent_name: "",
  agent_role: "AI receptionist",
  voice: "marin",
  default_language: "English",
  supported_languages_text: "",
  language_confirmation_prompt: "",
  greeting: "",
  business_context: "",
  transcription_prompt: "",
  inbound_script: "",
  outbound_script: "",
  extra_prompt_instructions: "",
  opening_flow_text: "",
  phone_call_style_rules_text: "",
  services_text: "",
  appointment_flow_text: "",
  objection_handling_text: "",
  escalation_rules_text: "",
  safety_relevant_services_text: "",
};

function buildEmptyErrors() {
  return {
    name: "",
    company_name: "",
    company_timezone: "",
    inbound_script: "",
    outbound_script: "",
  };
}

function listToTextarea(values) {
  return Array.isArray(values) ? values.join("\n") : "";
}

function mapToTextarea(mapping) {
  if (!mapping || typeof mapping !== "object" || Array.isArray(mapping)) {
    return "";
  }

  return Object.entries(mapping)
    .map(([key, value]) => `${key}: ${value}`)
    .join("\n");
}

function normalizeConfigToForm(config) {
  if (!config) {
    return EMPTY_FORM;
  }

  return {
    id: config.id || "",
    client_id: config.client_id ? String(config.client_id) : "",
    name: config.name || "",
    status: config.status || "ACTIVE",
    is_default: Boolean(config.is_default),
    company_name: config.company_name || "",
    company_timezone: config.company_timezone || "Asia/Calcutta",
    agent_name: config.agent_name || "",
    agent_role: config.agent_role || "AI receptionist",
    voice: config.voice || "marin",
    default_language: config.default_language || "English",
    supported_languages_text: listToTextarea(config.supported_languages),
    language_confirmation_prompt: config.language_confirmation_prompt || "",
    greeting: config.greeting || "",
    business_context: config.business_context || "",
    transcription_prompt: config.transcription_prompt || "",
    inbound_script: config.inbound_script || "",
    outbound_script: config.outbound_script || "",
    extra_prompt_instructions: config.extra_prompt_instructions || "",
    opening_flow_text: listToTextarea(config.opening_flow),
    phone_call_style_rules_text: listToTextarea(config.phone_call_style_rules),
    services_text: listToTextarea(config.services),
    appointment_flow_text: listToTextarea(config.appointment_flow),
    objection_handling_text: mapToTextarea(config.objection_handling),
    escalation_rules_text: listToTextarea(config.escalation_rules),
    safety_relevant_services_text: listToTextarea(config.safety_relevant_services),
  };
}

function buildPayload(form) {
  return {
    client_id: form.client_id ? Number(form.client_id) : null,
    name: form.name.trim(),
    status: form.status,
    is_default: Boolean(form.is_default),
    company_name: form.company_name.trim(),
    company_timezone: form.company_timezone.trim(),
    agent_name: form.agent_name.trim(),
    agent_role: form.agent_role.trim(),
    voice: form.voice.trim(),
    default_language: form.default_language.trim(),
    supported_languages: form.supported_languages_text,
    language_confirmation_prompt: form.language_confirmation_prompt.trim(),
    greeting: form.greeting.trim(),
    business_context: form.business_context.trim(),
    transcription_prompt: form.transcription_prompt.trim(),
    inbound_script: form.inbound_script.trim(),
    outbound_script: form.outbound_script.trim(),
    extra_prompt_instructions: form.extra_prompt_instructions.trim(),
    opening_flow: form.opening_flow_text,
    phone_call_style_rules: form.phone_call_style_rules_text,
    services: form.services_text,
    appointment_flow: form.appointment_flow_text,
    objection_handling: form.objection_handling_text,
    escalation_rules: form.escalation_rules_text,
    safety_relevant_services: form.safety_relevant_services_text,
  };
}

function validateForm(form) {
  const errors = buildEmptyErrors();

  if (!String(form.name || "").trim()) {
    errors.name = "Configuration name is required.";
  }
  if (!String(form.company_name || "").trim()) {
    errors.company_name = "Company name is required.";
  }
  if (!String(form.company_timezone || "").trim()) {
    errors.company_timezone = "Company timezone is required.";
  }
  if (!String(form.inbound_script || "").trim()) {
    errors.inbound_script = "Inbound script is required.";
  }
  if (!String(form.outbound_script || "").trim()) {
    errors.outbound_script = "Outbound script is required.";
  }

  return errors;
}

function getStatusVariant(config = {}) {
  if (config.is_default) {
    return "booked";
  }
  if (config.status === "ARCHIVED") {
    return "cancelled";
  }
  if (config.status === "INACTIVE") {
    return "info";
  }
  return "blue";
}

function getScopeLabel(config = {}, clientNameById = new Map()) {
  if (config.is_default) {
    return "Default fallback scope";
  }

  if (config.client_id) {
    return clientNameById.get(String(config.client_id)) || `Client ${config.client_id}`;
  }

  return "Global scope";
}

export default function AiRuntimeConfigManager({ showToast }) {
  const mountedRef = useRef(false);
  const [configsData, setConfigsData] = useState({ items: [], meta: {} });
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState(buildEmptyErrors());
  const [previewConfig, setPreviewConfig] = useState(null);
  const [actionBusyId, setActionBusyId] = useState("");

  const clientNameById = useMemo(
    () =>
      new Map(
        clients.map((client) => [
          String(client.client_id),
          client.name || `Client ${client.client_id}`,
        ])
      ),
    [clients]
  );

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const [configsResult, clientsResult] = await Promise.all([
        getAiConfigs({ page: 1, limit: 100 }),
        getClients(),
      ]);

      if (!mountedRef.current) {
        return;
      }

      setConfigsData(configsResult);
      setClients(clientsResult);
    } catch (errorValue) {
      const normalizedError = normalizeApiError(errorValue);
      if (!normalizedError.isAbort && mountedRef.current) {
        setError(normalizedError.message || "Unable to load AI runtime configurations.");
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    loadData().catch(() => undefined);

    return () => {
      mountedRef.current = false;
    };
  }, [loadData]);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setFormErrors(buildEmptyErrors());
    setPreviewConfig(null);
  };

  const handleEdit = async (configId) => {
    setActionBusyId(String(configId));

    try {
      const config = await getAiConfig(configId);
      if (!mountedRef.current || !config) {
        return;
      }

      setForm(normalizeConfigToForm(config));
      setFormErrors(buildEmptyErrors());
      setPreviewConfig(config);
    } catch (errorValue) {
      const normalizedError = normalizeApiError(errorValue);
      if (!normalizedError.isAbort) {
        showToast(normalizedError.message, true, 5000);
      }
    } finally {
      if (mountedRef.current) {
        setActionBusyId("");
      }
    }
  };

  const handlePreview = async (configId) => {
    setActionBusyId(String(configId));

    try {
      const config = await getAiConfig(configId);
      if (mountedRef.current) {
        setPreviewConfig(config || null);
      }
    } catch (errorValue) {
      const normalizedError = normalizeApiError(errorValue);
      if (!normalizedError.isAbort) {
        showToast(normalizedError.message, true, 5000);
      }
    } finally {
      if (mountedRef.current) {
        setActionBusyId("");
      }
    }
  };

  const handleArchive = async (configId) => {
    if (!window.confirm("Archive this AI runtime configuration?")) {
      return;
    }

    setActionBusyId(String(configId));
    try {
      await deleteAiConfig(configId);
      showToast("AI runtime configuration archived successfully.", false, 4000);
      await loadData();
      if (mountedRef.current && previewConfig?.id === configId) {
        setPreviewConfig(null);
      }
    } catch (errorValue) {
      const normalizedError = normalizeApiError(errorValue);
      if (!normalizedError.isAbort) {
        showToast(normalizedError.message, true, 5000);
      }
    } finally {
      if (mountedRef.current) {
        setActionBusyId("");
      }
    }
  };

  const handleStatusToggle = async (config) => {
    const nextStatus = config.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    setActionBusyId(String(config.id));

    try {
      await updateAiConfig(config.id, { status: nextStatus });
      showToast(
        nextStatus === "ACTIVE"
          ? "Configuration activated. Any other active configuration in the same scope was set to inactive."
          : "Configuration set to inactive.",
        false,
        5000
      );
      await loadData();
      if (mountedRef.current && previewConfig?.id === config.id) {
        setPreviewConfig(await getAiConfig(config.id));
      }
    } catch (errorValue) {
      const normalizedError = normalizeApiError(errorValue);
      if (!normalizedError.isAbort) {
        showToast(normalizedError.message, true, 5000);
      }
    } finally {
      if (mountedRef.current) {
        setActionBusyId("");
      }
    }
  };

  const handleSubmit = async () => {
    const nextErrors = validateForm(form);
    setFormErrors(nextErrors);
    if (Object.values(nextErrors).some(Boolean)) {
      return;
    }

    setSaving(true);
    try {
      const payload = buildPayload(form);
      const saved = form.id
        ? await updateAiConfig(form.id, payload)
        : await createAiConfig(payload);

      showToast(
        payload.status === "ACTIVE"
          ? "AI runtime configuration published and activated. Any other active configuration in the same scope was set to inactive."
          : form.id
            ? "AI runtime configuration updated successfully."
            : "AI runtime configuration created successfully.",
        false,
        4000
      );
      if (mountedRef.current) {
        setPreviewConfig(saved);
      }
      resetForm();
      await loadData();
    } catch (errorValue) {
      const normalizedError = normalizeApiError(errorValue);
      if (!normalizedError.isAbort) {
        showToast(normalizedError.message, true, 5000);
      }
    } finally {
      if (mountedRef.current) {
        setSaving(false);
      }
    }
  };

  return (
    <>
      <section id="ai-runtime-config" className="chart-card settings-panel settings-panel-span-full">
        <div className="settings-panel-header">
          <div className="team-card-heading">AI Runtime Configuration</div>
          <div className="page-subtitle">
            Manage client-specific company details, voice, languages, inbound and outbound
            flows, and publish both the agent profile JSON and system prompt to S3 without
            changing code or environment files.
          </div>
        </div>

        <div className="settings-script-summary">
          <div>
            <div className="settings-label">Runtime Source Of Truth</div>
            <div className="settings-script-summary-text">
              Server 1 resolves the active profile and prompt from the database and S3 by
              campaign, client, or inbound number. Local config files remain fallback only.
            </div>
          </div>
          <Badge variant="blue">{configsData.items.length} configs</Badge>
        </div>

        <div className="settings-inline-help" style={{ marginBottom: "12px" }}>
          Only one configuration can be <strong>ACTIVE</strong> for a given client scope or for
          the default fallback scope. Saving or activating another configuration in the same
          scope automatically sets the existing active one to <strong>INACTIVE</strong>.
        </div>

        <div className="settings-fields">
          <div className="settings-script-grid">
            <label className="settings-field">
              <span className="settings-label">Configuration Name</span>
              <input
                className="settings-input"
                value={form.name}
                onChange={(event) =>
                  setForm((current) => ({ ...current, name: event.target.value }))
                }
                placeholder="Primary client voice configuration"
              />
              {formErrors.name ? (
                <span className="settings-inline-error">{formErrors.name}</span>
              ) : null}
            </label>

            <label className="settings-field">
              <span className="settings-label">Client</span>
              <select
                className="settings-select"
                value={form.client_id}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    client_id: event.target.value,
                    is_default: event.target.value ? current.is_default : true,
                  }))
                }
              >
                <option value="">Default / global fallback</option>
                {clients.map((client) => (
                  <option key={client.client_id} value={client.client_id}>
                    {client.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="settings-field">
              <span className="settings-label">Status</span>
              <select
                className="settings-select"
                value={form.status}
                onChange={(event) =>
                  setForm((current) => ({ ...current, status: event.target.value }))
                }
              >
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
              </select>
            </label>

            <label className="settings-field">
              <span className="settings-label">Company Name</span>
              <input
                className="settings-input"
                value={form.company_name}
                onChange={(event) =>
                  setForm((current) => ({ ...current, company_name: event.target.value }))
                }
                placeholder="Example Client Company"
              />
              {formErrors.company_name ? (
                <span className="settings-inline-error">{formErrors.company_name}</span>
              ) : null}
            </label>

            <label className="settings-field">
              <span className="settings-label">Company Timezone</span>
              <input
                className="settings-input"
                value={form.company_timezone}
                onChange={(event) =>
                  setForm((current) => ({ ...current, company_timezone: event.target.value }))
                }
                placeholder="Asia/Calcutta"
              />
              {formErrors.company_timezone ? (
                <span className="settings-inline-error">{formErrors.company_timezone}</span>
              ) : null}
            </label>

            <label className="settings-field">
              <span className="settings-label">Agent Name</span>
              <input
                className="settings-input"
                value={form.agent_name}
                onChange={(event) =>
                  setForm((current) => ({ ...current, agent_name: event.target.value }))
                }
                placeholder="Example Client AI Receptionist"
              />
            </label>

            <label className="settings-field">
              <span className="settings-label">Agent Role / Persona</span>
              <input
                className="settings-input"
                value={form.agent_role}
                onChange={(event) =>
                  setForm((current) => ({ ...current, agent_role: event.target.value }))
                }
                placeholder="AI receptionist"
              />
            </label>

            <label className="settings-field">
              <span className="settings-label">Voice</span>
              <input
                className="settings-input"
                value={form.voice}
                onChange={(event) =>
                  setForm((current) => ({ ...current, voice: event.target.value }))
                }
                placeholder="marin"
              />
            </label>

            <label className="settings-field">
              <span className="settings-label">Default Language</span>
              <input
                className="settings-input"
                value={form.default_language}
                onChange={(event) =>
                  setForm((current) => ({ ...current, default_language: event.target.value }))
                }
                placeholder="English"
              />
            </label>

            <label className="settings-field">
              <span className="settings-label">Supported Languages</span>
              <textarea
                className="settings-input settings-textarea"
                rows="4"
                value={form.supported_languages_text}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    supported_languages_text: event.target.value,
                  }))
                }
                placeholder={"English\nHindi\nTamil"}
              />
            </label>
          </div>

          <label className="settings-toggle-row">
            <span>Use as default fallback configuration</span>
            <input
              type="checkbox"
              checked={Boolean(form.is_default)}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  is_default: event.target.checked,
                  client_id: event.target.checked ? "" : current.client_id,
                }))
              }
            />
          </label>

          <label className="settings-field">
            <span className="settings-label">Greeting</span>
            <textarea
              className="settings-input settings-textarea"
              rows="4"
              value={form.greeting}
              onChange={(event) =>
                setForm((current) => ({ ...current, greeting: event.target.value }))
              }
              placeholder="Hello, this is the AI receptionist from ..."
            />
          </label>

          <label className="settings-field">
            <span className="settings-label">Language Confirmation Prompt</span>
            <textarea
              className="settings-input settings-textarea"
              rows="3"
              value={form.language_confirmation_prompt}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  language_confirmation_prompt: event.target.value,
                }))
              }
              placeholder="Would you prefer English, Hindi, or Arabic?"
            />
          </label>

          <label className="settings-field">
            <span className="settings-label">Business Context</span>
            <textarea
              className="settings-input settings-textarea"
              rows="5"
              value={form.business_context}
              onChange={(event) =>
                setForm((current) => ({ ...current, business_context: event.target.value }))
              }
              placeholder="Describe the business, target outcomes, and what the AI should help callers with."
            />
          </label>

          <div className="settings-script-grid">
            <label className="settings-field">
              <span className="settings-label">Opening Flow</span>
              <textarea
                className="settings-input settings-textarea"
                rows="6"
                value={form.opening_flow_text}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    opening_flow_text: event.target.value,
                  }))
                }
                placeholder={"First assistant turn only: ...\nIf caller agrees ...\nIf caller is busy ..."}
              />
            </label>

            <label className="settings-field">
              <span className="settings-label">Phone Call Style Rules</span>
              <textarea
                className="settings-input settings-textarea"
                rows="6"
                value={form.phone_call_style_rules_text}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    phone_call_style_rules_text: event.target.value,
                  }))
                }
                placeholder={"Keep replies concise.\nAsk one question at a time.\nIf interrupted, stop and listen."}
              />
            </label>

            <label className="settings-field">
              <span className="settings-label">Services / Topics</span>
              <textarea
                className="settings-input settings-textarea"
                rows="6"
                value={form.services_text}
                onChange={(event) =>
                  setForm((current) => ({ ...current, services_text: event.target.value }))
                }
                placeholder={"Dental implants\nSkin treatment consultation\nInsurance policy renewal"}
              />
            </label>
          </div>

          <div className="settings-script-grid">
            <label className="settings-field">
              <span className="settings-label">Inbound Script</span>
              <textarea
                className="settings-input settings-textarea"
                rows="8"
                value={form.inbound_script}
                onChange={(event) =>
                  setForm((current) => ({ ...current, inbound_script: event.target.value }))
                }
                placeholder="How the AI should handle inbound callers for this client."
              />
              {formErrors.inbound_script ? (
                <span className="settings-inline-error">{formErrors.inbound_script}</span>
              ) : null}
            </label>

            <label className="settings-field">
              <span className="settings-label">Outbound Script</span>
              <textarea
                className="settings-input settings-textarea"
                rows="8"
                value={form.outbound_script}
                onChange={(event) =>
                  setForm((current) => ({ ...current, outbound_script: event.target.value }))
                }
                placeholder="How the AI should open and progress outbound calls for this client."
              />
              {formErrors.outbound_script ? (
                <span className="settings-inline-error">{formErrors.outbound_script}</span>
              ) : null}
            </label>
          </div>

          <div className="settings-script-grid">
            <label className="settings-field">
              <span className="settings-label">Appointment Flow</span>
              <textarea
                className="settings-input settings-textarea"
                rows="6"
                value={form.appointment_flow_text}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    appointment_flow_text: event.target.value,
                  }))
                }
                placeholder={"Ask for the caller name.\nAsk preferred day or time.\nConfirm callback details if needed."}
              />
            </label>

            <label className="settings-field">
              <span className="settings-label">Objection Handling</span>
              <textarea
                className="settings-input settings-textarea"
                rows="6"
                value={form.objection_handling_text}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    objection_handling_text: event.target.value,
                  }))
                }
                placeholder={"busy: Sure, what would be a better time?\nprice: The exact cost depends on assessment."}
              />
            </label>

            <label className="settings-field">
              <span className="settings-label">Escalation / Safety Rules</span>
              <textarea
                className="settings-input settings-textarea"
                rows="6"
                value={form.escalation_rules_text}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    escalation_rules_text: event.target.value,
                  }))
                }
                placeholder={"If caller requests a human, transfer or collect callback.\nIf caller reports emergency symptoms, advise urgent care."}
              />
            </label>
          </div>

          <div className="settings-script-grid">
            <label className="settings-field">
              <span className="settings-label">Safety-Relevant Services</span>
              <textarea
                className="settings-input settings-textarea"
                rows="5"
                value={form.safety_relevant_services_text}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    safety_relevant_services_text: event.target.value,
                  }))
                }
                placeholder={"Emergency consultation\nSurgical assessment"}
              />
            </label>

            <label className="settings-field">
              <span className="settings-label">Transcription Prompt</span>
              <textarea
                className="settings-input settings-textarea"
                rows="5"
                value={form.transcription_prompt}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    transcription_prompt: event.target.value,
                  }))
                }
                placeholder="Optional ASR guidance for the client vocabulary."
              />
            </label>

            <label className="settings-field">
              <span className="settings-label">Extra Prompt Instructions</span>
              <textarea
                className="settings-input settings-textarea"
                rows="5"
                value={form.extra_prompt_instructions}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    extra_prompt_instructions: event.target.value,
                  }))
                }
                placeholder="Any additional business-specific guardrails or instructions."
              />
            </label>
          </div>

          <div className="page-actions">
            <Button
              variant="gold"
              disabled={saving}
              onClick={() =>
                setPreviewConfig({
                  ...buildPayload(form),
                  id: form.id || "draft",
                  published_system_prompt_text: form.outbound_script || form.inbound_script,
                })
              }
            >
              Preview
            </Button>
            <Button variant="primary" disabled={saving} onClick={handleSubmit}>
              {saving ? "Publishing..." : form.id ? "Update And Publish" : "Create And Publish"}
            </Button>
            <Button disabled={saving} onClick={resetForm}>
              Clear
            </Button>
            <Button disabled={loading} onClick={() => loadData().catch(() => undefined)}>
              Refresh
            </Button>
          </div>
        </div>
      </section>

      <section className="chart-card settings-panel settings-panel-span-full">
        <div className="settings-panel-header">
          <div className="team-card-heading">Published Client Configurations</div>
          <div className="page-subtitle">
            Review what is currently published to S3 and available to the inbound and
            outbound runtime resolver.
          </div>
        </div>

        {loading ? (
          <div className="empty-state">
            <Skeleton style={{ height: 16, width: 220, margin: "0 auto 8px" }} />
            <Skeleton style={{ height: 12, width: 140, margin: "0 auto" }} />
          </div>
        ) : error ? (
          <EmptyState title="Unable to load AI runtime configurations" subtitle={error} />
        ) : !configsData.items.length ? (
          <EmptyState
            title="No AI runtime configurations"
            subtitle="Create and publish a client configuration to remove client-specific prompt and profile values from static files."
          />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Client</th>
                  <th>Status</th>
                  <th>Voice</th>
                  <th>Languages</th>
                  <th>Published</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {configsData.items.map((config) => {
                  const isBusy = actionBusyId === String(config.id);
                  return (
                    <tr key={config.id}>
                      <td>
                        <div>
                          <strong>{config.name}</strong>
                          <div className="muted-text">
                            {config.company_name || "Unnamed company"}
                          </div>
                        </div>
                      </td>
                      <td>
                        {config.is_default
                          ? "Default fallback"
                          : clientNameById.get(String(config.client_id)) || "Client not set"}
                        <div className="muted-text">{getScopeLabel(config, clientNameById)}</div>
                      </td>
                      <td>
                        <Badge variant={getStatusVariant(config)}>
                          {config.is_default ? "Default" : config.status}
                        </Badge>
                      </td>
                      <td>{config.voice || "marin"}</td>
                      <td>
                        {Array.isArray(config.supported_languages) &&
                        config.supported_languages.length
                          ? config.supported_languages.join(", ")
                          : config.default_language || "English"}
                      </td>
                      <td>{formatDateTime(config.published_at || config.updated_at)}</td>
                      <td className="td-actions">
                        <div className="settings-script-actions">
                          <Button disabled={isBusy} onClick={() => handlePreview(config.id)}>
                            Preview
                          </Button>
                          <Button disabled={isBusy} onClick={() => handleEdit(config.id)}>
                            Edit
                          </Button>
                          <Button
                            variant={config.status === "ACTIVE" ? "gold" : "primary"}
                            disabled={isBusy || config.status === "ARCHIVED"}
                            onClick={() => handleStatusToggle(config)}
                          >
                            {config.status === "ACTIVE" ? "Set Inactive" : "Activate"}
                          </Button>
                          <Button
                            variant="danger"
                            disabled={isBusy || config.status === "ARCHIVED"}
                            onClick={() => handleArchive(config.id)}
                          >
                            Archive
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="chart-card settings-panel settings-panel-span-full">
        <div className="settings-panel-header">
          <div className="team-card-heading">Published Assets Preview</div>
          <div className="page-subtitle">
            Review the generated system prompt text and the S3-backed asset references that
            server 1 will resolve at call time.
          </div>
        </div>

        {previewConfig ? (
          <div className="settings-script-preview">
            <div className="settings-script-summary">
              <div>
                <div className="settings-label">Selected Configuration</div>
                <div className="settings-script-summary-text">
                  <strong>{previewConfig.name || "Draft configuration"}</strong>
                  {previewConfig.company_name ? ` for ${previewConfig.company_name}` : ""}
                </div>
              </div>
              <Badge variant={getStatusVariant(previewConfig)}>
                {previewConfig.is_default ? "Default" : previewConfig.status || "Draft"}
              </Badge>
            </div>

            <div className="settings-script-grid">
              <div className="settings-field">
                <span className="settings-label">Agent Profile S3 URL</span>
                <div className="settings-inline-help">
                  {previewConfig.agent_profile_s3_public_url ||
                    previewConfig.agent_profile_s3_uri ||
                    "Will use a signed/private URL when resolved at runtime."}
                </div>
              </div>
              <div className="settings-field">
                <span className="settings-label">System Prompt S3 URL</span>
                <div className="settings-inline-help">
                  {previewConfig.system_prompt_s3_public_url ||
                    previewConfig.system_prompt_s3_uri ||
                    "Will use a signed/private URL when resolved at runtime."}
                </div>
              </div>
            </div>

            <pre className="settings-script-preview-content">
              {previewConfig.published_system_prompt_text ||
                previewConfig.outbound_script ||
                previewConfig.inbound_script ||
                "No published prompt text available yet."}
            </pre>
          </div>
        ) : (
          <EmptyState
            title="No configuration selected"
            subtitle="Preview a saved configuration or use the form preview to inspect the generated runtime content."
          />
        )}
      </section>
    </>
  );
}
